/*
 * ArticleCartierTransilvania.tsx — /cartier-transilvania-brasov
 *
 * Design Philosophy: Editorial Minimalism with Architectural Precision
 * - Same visual language as existing articles: Playfair Display headings, Source Sans 3 body
 * - Muted earth tones, generous whitespace, data tables for price reference
 * - Editorial article with mention — Cartier Transilvania featured as a concrete example
 * - Keywords: apartments Brașov, apartments for sale Brașov Romania, reliable developer
 */

import { useState } from "react";
import { Clock, Share2, MapPin, ExternalLink, CheckCircle, Check } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { subscribeToNewsletter } from "@/lib/brevo";

const HERO_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/uLiraOubpeeGNSzh.webp";

const ARTICLE1_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article1-v1-mBURY4muT8nNAjUnDwQRbz.webp";
const ARTICLE4_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article4-market-hero_bed3bdbc.webp";
const ARTICLE5_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article5-entertainment-hero-YwxKL9nvfxZqDmSm9YR8ym.webp";

const RELATED = [
  { tag: "Relocation", title: "Cost of Living in Brașov 2026: the honest numbers, neighbourhood by neighbourhood", img: ARTICLE4_IMG, href: "/cost-of-living-brasov" },
  { tag: "City Guide", title: "Brașov, straight up: beyond the bear photos and the mountain air clichés", img: ARTICLE1_IMG, href: "/why-expats-choose-brasov" },
  { tag: "Lifestyle", title: "Entertainment in Brașov: Day & Night for Adults — with Real Prices", img: ARTICLE5_IMG, href: "/entertainment-brasov-guide" },
];

function NewsletterInlineLight() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle"|"loading"|"success"|"error">("idle");
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await subscribeToNewsletter(email);
      setStatus("success");
    } catch { setStatus("error"); }
  };
  if (status === "success") return <p className="text-xs text-green-700 py-2">✓ Subscribed! See you Friday.</p>;
  return (
    <form onSubmit={handleSubmit}>
      <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="w-full border border-stone-200 px-3 py-2 text-xs text-stone-700 outline-none focus:border-stone-400 mb-2" />
      <button type="submit" disabled={status === "loading"} className="w-full py-2 text-xs text-white font-semibold disabled:opacity-60" style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
        {status === "loading" ? "..." : "SUBSCRIBE"}
      </button>
      {status === "error" && <p className="text-xs text-red-500 mt-1">Try again.</p>}
    </form>
  );
}

function ShareButton({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button onClick={handleShare} className="flex items-center gap-2 text-xs text-stone-500 hover:text-stone-800 transition-colors">
      {copied ? <Check size={13} className="text-green-600" /> : <Share2 size={13} />}
      {copied ? "Copied!" : "Share"}
    </button>
  );
}

export default function ArticleCartierTransilvania() {
  return (
    <>
    <Helmet>
      <title>Apartments for Sale in Brașov: Cartier Transilvania, Noua Neighbourhood</title>
      <meta name="description" content="Apartments for sale in Brașov: Cartier Transilvania, 208 apartments in Noua. T1–T3 completed, T4–T6 delivering September 2027. Solar panels, heat pump, Lake Noua." />
      <meta name="keywords" content="apartments for sale Brasov, Brasov new apartments, Cartier Transilvania Brasov, buy apartment Brasov Romania, Noua Brasov real estate" />
      <meta property="og:title" content="Apartments for Sale in Brașov: Cartier Transilvania, Noua Neighbourhood" />
      <meta property="og:description" content="Apartments for sale in Brașov: Cartier Transilvania, 208 apartments in Noua. T1–T3 completed, T4–T6 delivering September 2027. Solar panels, heat pump, Lake Noua." />
      <meta property="og:image" content="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/uLiraOubpeeGNSzh.webp" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="article" />
      <meta property="og:url" content="https://brasovgateway.com/cartier-transilvania-brasov" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Apartments for Sale in Brașov: Cartier Transilvania, Noua Neighbourhood" />
      <link rel="canonical" href="https://brasovgateway.com/cartier-transilvania-brasov" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": "https://brasovgateway.com/cartier-transilvania-brasov#article",
              "headline": "Buying an Apartment in Brașov in 2026: What to Look For, and One Development Worth Knowing",
              "description": "In-depth review of Cartier Transilvania — 208 apartments in Noua, Brașov. T1–T3 completed, T4–T6 delivering September 2027.",
              "image": "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article6-cartier-transilvania-hero-DNYfjuzphStLCLmEo2tjfv.webp",
              "datePublished": "2026-04-05",
              "dateModified": "2026-04-05",
              "author": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "url": "https://brasovgateway.com/cartier-transilvania-brasov",
              "inLanguage": "en",
              "articleSection": "Real Estate"
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://brasovgateway.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": "https://brasovgateway.com/blog"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Buying an Apartment in Brașov in 2026: What to Look For, and...",
                  "item": "https://brasovgateway.com/cartier-transilvania-brasov"
                }
              ]
            }
          ]
        }
      `}</script>
    </Helmet>
    <div className="min-h-screen" style={{ fontFamily: "Source Sans 3, sans-serif", backgroundColor: "oklch(0.985 0.006 75)" }}>
      <SiteHeader />

      {/* Hero image */}
      <div className="relative h-72 md:h-[480px] mt-16">
        <img
          src={HERO_IMG}
          alt="Apartments for sale in Brașov — Cartier Transilvania residential complex, Noua"
          className="w-full h-full object-cover"
          width="1024"
          height="576"
          fetchPriority="high"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, oklch(0.985 0.006 75) 100%)" }} />
      </div>

      <div className="container py-8">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Main content */}
          <main className="flex-1 min-w-0">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs uppercase tracking-widest font-semibold px-2 py-1" style={{ color: "oklch(0.38 0.12 15)", backgroundColor: "oklch(0.95 0.02 20)" }}>
                  Real Estate
                </span>
                <div className="flex items-center gap-1 text-stone-400 text-xs">
                  <Clock size={11} />
                  <span>8 min read</span>
                </div>
              </div>
              <h1 className="text-stone-900 mb-4 leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 700 }}>
                Apartments for sale in Brașov: what to look for, and one development worth knowing
              </h1>
              <div className="flex items-center justify-between py-4 border-t border-b border-stone-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                    <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Brașov Gateway" className="w-full h-full object-cover opacity-50" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-stone-900">Brașov Gateway Editorial</p>
                    <p className="text-xs text-stone-500">Real Estate Team</p>
                  </div>
                </div>
                <ShareButton url="https://brasovgateway.com/cartier-transilvania-brasov" title="Cartier Transilvania Brașov: Ce trebuie să știi" />
              </div>
            </div>

            {/* Article body */}
            <div>
              <p className="text-stone-700 text-lg font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>                Searching for apartments for sale in Brașov? The question we hear most often from people seriously considering a move is not “should I buy?” — it is “who do I buy from?”" In a market where new residential developments have multiplied rapidly over the past decade, the quality gap between developers has become the single most important variable in the buying decision.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                This article covers what to look for when buying apartments for sale in Brașov, Romania in 2026 — and uses one specific development, Cartier Transilvania, as a worked example of what a reliable developer actually looks like in practice.
              </p>

              {/* Section 1 */}
              <h2 id="market-context" className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                The Brașov apartment market in 2026: still moving
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Brașov is currently the second most expensive city in Romania for residential property, behind Cluj-Napoca and just ahead of Bucharest. Average asking prices for new builds reached <strong>2,228 EUR per square metre</strong> at the start of 2026 — a 9% increase year-on-year. For context on how this fits into the broader cost of living, see our <Link href="/cost-of-living-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>cost of living guide for Brașov</Link>. Demand is being driven by a combination of domestic buyers upgrading from older communist-era stock, remote workers relocating from Bucharest, and a growing number of foreign buyers drawn by the city's quality of life and relatively accessible entry prices compared to Western Europe.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                The areas showing the strongest price growth are the newer residential zones on the southern and eastern edges of the city — particularly the Noua, Tractorul, and Coresi districts — where large-scale developments have created genuine neighbourhoods with infrastructure, green space, and community amenities. These areas now compete seriously with the historic centre for buyers who prioritise livability over postcard views.
              </p>

              {/* Price table */}
              <div className="my-6 overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                      <th className="text-left px-4 py-3 text-stone-600 font-semibold text-xs uppercase tracking-wider border-b border-stone-200">Area</th>
                      <th className="text-right px-4 py-3 text-stone-600 font-semibold text-xs uppercase tracking-wider border-b border-stone-200">Price / m² (new build)</th>
                      <th className="text-right px-4 py-3 text-stone-600 font-semibold text-xs uppercase tracking-wider border-b border-stone-200">1BR (40–50 m²)</th>
                      <th className="text-right px-4 py-3 text-stone-600 font-semibold text-xs uppercase tracking-wider border-b border-stone-200">2BR (60–75 m²)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { area: "Centrul Istoric / Schei", sqm: "3,000–3,500 €", one: "130,000–160,000 €", two: "190,000–240,000 €" },
                      { area: "Kasper / Tractorul", sqm: "2,400–2,800 €", one: "105,000–130,000 €", two: "155,000–195,000 €" },
                      { area: "Coresi / Noua", sqm: "2,000–2,400 €", one: "85,000–110,000 €", two: "125,000–165,000 €" },
                      { area: "Griviței / outskirts", sqm: "1,600–2,000 €", one: "68,000–90,000 €", two: "100,000–140,000 €" },
                    ].map((row, i) => (
                      <tr key={i} style={{ backgroundColor: i % 2 !== 0 ? "oklch(0.985 0.006 75)" : "white" }}>
                        <td className="px-4 py-3 text-stone-700 border-b border-stone-100">{row.area}</td>
                        <td className="px-4 py-3 text-right text-stone-600 border-b border-stone-100 font-mono text-xs">{row.sqm}</td>
                        <td className="px-4 py-3 text-right text-stone-600 border-b border-stone-100 font-mono text-xs">{row.one}</td>
                        <td className="px-4 py-3 text-right text-stone-600 border-b border-stone-100 font-mono text-xs">{row.two}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-stone-500 text-xs font-light mb-8">Source: Imobiliare.ro, Brașov Gateway research, Q1 2026. Prices are indicative and vary by floor, finish, and specific building.</p>

              {/* Section 2 */}
              <h2 id="developer-checklist" className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                How to evaluate a developer: the checklist that matters
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Romania's real estate market has matured significantly, but due diligence remains essential. Before signing a pre-sale contract on any new development in Brașov, these are the questions worth asking:
              </p>

              <div className="my-6 space-y-3">
                {[
                  { title: "Track record", text: "Has the developer delivered previous projects on time? Look for completed phases, not just renderings. A developer with buildings already occupied and residents living in them is a fundamentally different proposition from one selling off-plan for the first time." },
                  { title: "Financial structure", text: "Is the project financed through bank credit or pre-sales? Bank-financed projects carry lower completion risk. Ask to see the construction authorisation (autorizație de construire) and the land title (extras de carte funciară)." },
                  { title: "Energy performance", text: "Romania's building code has tightened considerably. New buildings should have an energy performance certificate of class A or A+. Ask specifically about heating systems — heat pumps and solar panels reduce long-term running costs significantly." },
                  { title: "Delivery timeline and penalties", text: "Pre-sale contracts should specify a delivery date and include penalty clauses for delays. Vague language like 'estimated completion' without a contractual date is a red flag." },
                  { title: "Community infrastructure", text: "A development is not just a building. Parking ratios, green space, proximity to schools, medical facilities, and public transport all affect both livability and resale value." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 p-4 border border-stone-200 bg-white">
                    <CheckCircle size={18} className="flex-shrink-0 mt-0.5" style={{ color: "oklch(0.38 0.12 15)" }} />
                    <div>
                      <p className="text-stone-800 font-semibold text-sm mb-1">{item.title}</p>
                      <p className="text-stone-600 text-sm font-light leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Section 3 — Cartier Transilvania */}
              <h2 id="cartier-transilvania" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Cartier Transilvania: a development worth knowing
              </h2>

              {/* Partner mention banner */}
              <div className="my-6 border border-stone-200 overflow-hidden">
                <div className="px-4 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                  <span className="text-xs text-stone-400 uppercase tracking-widest">Featured development</span>
                </div>
                <div className="p-5 flex flex-col sm:flex-row gap-5 bg-white">
                  <img src={HERO_IMG} alt="Ansamblu rezidențial Cartier Transilvania Brașov" className="w-full sm:w-56 h-36 object-cover flex-shrink-0" loading="lazy" />
                  <div>
                    <p className="text-xs uppercase tracking-widest font-semibold mb-1" style={{ color: "oklch(0.38 0.12 15)" }}>Cartier Transilvania — Brașov</p>
                    <h4 className="font-bold text-stone-900 mb-2 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
                      208 apartments. 6 blocks. 15 years of delivered projects.
                    </h4>
                    <p className="text-stone-500 text-sm font-light leading-relaxed mb-3">
                      Located next to Lake Noua, the Zoo and Adventure Park. T1, T2, T3 completed and occupied. T4, T5, T6 under construction — delivery September 2027.
                    </p>
                    <a href="https://www.cartiertransilvania.ro/en/" target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-white px-4 py-2 transition-opacity hover:opacity-90"
                      style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
                      VIEW PROJECT <ExternalLink size={11} />
                    </a>
                  </div>
                </div>
              </div>

              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                When we apply the checklist above to Cartier Transilvania, the picture is unusually clear. The developer — operating under Transilvania JUU TAKU Group SRL — has been building in the Noua district of Brașov for <strong>15 years</strong>. That is not a marketing claim; it is verifiable in the completed buildings that already exist on site.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                The project comprises <strong>208 apartments across six blocks</strong> (T1 through T6). The first three blocks are completed, handed over, and occupied — meaning buyers of T4, T5, and T6 can walk through the finished product before signing anything. That is a significant advantage in a market where most off-plan purchases are made from renderings alone.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                T4, T5, and T6 are currently under construction with a contractual delivery date of <strong>September 2027</strong>. The development is positioned in the Noua area — one of Brașov's most livable zones, with direct access to Lake Noua, the city zoo, and the Adventure Park, while remaining well-connected to the city centre by public transport.
              </p>

              {/* Key features */}
              <h3 id="key-features" className="text-stone-900 mt-8 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.2rem", fontWeight: 700 }}>
                What the development offers
              </h3>
              <div className="my-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { label: "Energy efficiency", desc: "Solar panels and heat pumps — lower running costs, no dependence on district heating (termoficare)" },
                  { label: "Green buildings", desc: "Designed to meet current Romanian energy performance standards, with generous green spaces throughout the development" },
                  { label: "Intelligent floor plans", desc: "Apartments designed by architects specifically to maximise usable space — efficient compartimentare for various family configurations" },
                  { label: "Community amenities", desc: "State kindergarten, medical clinic, and gym accessible within or adjacent to the development" },
                  { label: "Natural setting", desc: "Steps from Lake Noua, the Zoo, and Parc Aventura — a rare combination of urban access and genuine green space" },
                  { label: "Proven developer", desc: "15 years of completed projects in the same neighbourhood — the most reliable indicator of delivery capability" },
                ].map((f, i) => (
                  <div key={i} className="p-4 border border-stone-200 bg-white">
                    <p className="text-stone-800 font-semibold text-sm mb-1">{f.label}</p>
                    <p className="text-stone-500 text-sm font-light leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>

              {/* Section 4 — Location */}
              <h2 id="noua-district" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Why the Noua district makes sense for buyers in 2026
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Five years ago, Noua was considered a peripheral choice. Today it is one of the most sought-after areas for families and young professionals buying their first or second apartment in Brașov. The reasons are straightforward: newer building stock, better energy performance, lower price per square metre than the historic centre, and a genuine neighbourhood feel that older central areas — with their tourist-facing economy — increasingly lack.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                The area has also attracted significant commercial investment. Retail, restaurants, and services have followed the residential growth, reducing the dependency on the city centre for daily needs. For families with children, the proximity to schools, the zoo, and outdoor recreation areas is a practical advantage that shows up in both quality of life and resale values.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                From an investment perspective, Noua and the surrounding zone currently offer the best combination of yield and capital appreciation potential in Brașov. Entry prices remain below the historic centre, but the gap has been narrowing consistently — which is exactly the dynamic that creates long-term value for buyers who move early.
              </p>

              {/* Section 5 — Practical advice */}
              <h2 id="practical-steps" className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Practical steps for foreign buyers
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                EU citizens can buy property in Romania on the same terms as Romanian nationals. Non-EU citizens can buy apartments without restriction; land purchases involve additional requirements. The purchase process typically involves:
              </p>
              <ol className="list-decimal list-inside space-y-3 mb-6 text-stone-600 font-light" style={{ lineHeight: "1.8" }}>
                <li className="pl-2"><strong className="text-stone-800">Pre-sale agreement (antecontract)</strong> — signed with a notary, specifying price, delivery date, and penalty clauses. A deposit of 10–20% is standard.</li>
                <li className="pl-2"><strong className="text-stone-800">Due diligence</strong> — verify the land title (extras de carte funciară), construction authorisation, and developer's financial standing. A local lawyer is worth the fee.</li>
                <li className="pl-2"><strong className="text-stone-800">Final contract (contract de vânzare-cumpărare)</strong> — signed at a notary upon completion. The notary fee is approximately 0.5–1% of the purchase price.</li>
                <li className="pl-2"><strong className="text-stone-800">Registration</strong> — the notary registers the transfer with the Land Registry (Cartea Funciară). This takes 1–3 business days.</li>
                <li className="pl-2"><strong className="text-stone-800">Tax</strong> — property transfer tax in Romania is 3% of the declared value for properties sold within 3 years of acquisition, and 1% thereafter. VAT (19%) applies to new builds purchased from developers.</li>
              </ol>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                If you are buying from abroad, a power of attorney (procură notarială) allows a local representative to sign on your behalf — a common arrangement for non-resident buyers. Brașov Gateway can connect you with English-speaking lawyers and notaries who handle international transactions regularly.
              </p>

              {/* CTA box */}
              <div className="my-10 p-6 border border-stone-200 bg-white">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest font-semibold mb-1" style={{ color: "oklch(0.38 0.12 15)" }}>Interested in Cartier Transilvania?</p>
                    <h4 className="font-bold text-stone-900 mb-1 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
                      We can arrange a site visit and connect you with the sales team.
                    </h4>
                    <p className="text-stone-500 text-sm font-light">No obligation. We speak English, Italian, Hungarian, German, and Romanian.</p>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <Link href="/contact">
                      <button className="text-xs text-white px-5 py-2.5 transition-opacity hover:opacity-90 whitespace-nowrap w-full"
                        style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
                        GET IN TOUCH
                      </button>
                    </Link>
                    <a href="https://www.cartiertransilvania.ro/en/" target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-1 text-xs text-stone-600 border border-stone-300 px-5 py-2.5 hover:bg-stone-50 transition-colors whitespace-nowrap"
                      style={{ letterSpacing: "0.06em" }}>
                      VIEW PROJECT SITE <ExternalLink size={10} />
                    </a>
                  </div>
                </div>
              </div>

              {/* Closing */}
              <h2 id="bottom-line" className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                The bottom line
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Buying an apartment in Brașov in 2026 is still a sound decision for most buyer profiles — whether you are relocating, investing, or planning ahead. For a step-by-step overview of the purchase process as a foreigner, see our guide on <Link href="/buying-apartment-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>buying property in Brașov in 2026</Link>. The market has matured, prices have risen, but the fundamentals remain strong: a growing city, a limited supply of quality new builds, and a quality of life that is genuinely difficult to replicate at this price point in Europe.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                The key variable is not the market — it is the developer. In a city where new projects launch regularly, the ability to walk through completed buildings before signing a contract is not a small thing. It is the clearest possible evidence that a developer delivers on what they promise. That is what makes Cartier Transilvania worth a serious look for anyone searching for <strong>apartments for sale in Brașov, Romania</strong> in 2026.
              </p>
            </div>

            {/* Related articles */}
            <div className="mt-16 pt-10 border-t border-stone-200">
              <p className="text-xs uppercase tracking-widest font-semibold text-stone-400 mb-6">Related Articles</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {RELATED.map((art, i) => (
                  <Link key={i} href={art.href}>
                    <div className="group cursor-pointer border border-stone-200 overflow-hidden hover:border-stone-400 transition-colors bg-white">
                      <div className="overflow-hidden h-36">
                        <img src={art.img} alt={art.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </div>
                      <div className="p-4">
                        <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>{art.tag}</p>
                        <p className="text-stone-800 text-sm font-semibold leading-snug line-clamp-3" style={{ fontFamily: "Playfair Display, serif" }}>{art.title}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="mt-6 text-center">
                <Link href="/blog">
                  <button className="text-xs uppercase tracking-widest text-stone-500 hover:text-stone-800 transition-colors border border-stone-300 px-6 py-2.5 hover:border-stone-500">
                    View all articles →
                  </button>
                </Link>
              </div>
            </div>
          </main>

          {/* Sidebar */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="sticky top-24 space-y-6">
            {/* In this article */}
            <div className="border border-stone-200 p-5 bg-white">
              <p className="text-xs uppercase tracking-widest font-semibold text-stone-400 mb-4">In this article</p>
              <nav className="space-y-2">
                {[
                  { id: "market-context", label: "The Brașov market in 2026" },
                  { id: "developer-checklist", label: "How to evaluate a developer" },
                  { id: "cartier-transilvania", label: "Cartier Transilvania" },
                  { id: "key-features", label: "What the development offers" },
                  { id: "noua-district", label: "Why Noua makes sense" },
                  { id: "practical-steps", label: "Practical steps for buyers" },
                  { id: "bottom-line", label: "The bottom line" },
                ].map((item) => (
                  <a key={item.id} href={`#${item.id}`}
                    onClick={(e) => { e.preventDefault(); document.getElementById(item.id)?.scrollIntoView({ behavior: "smooth" }); }}
                    className="flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 transition-colors cursor-pointer py-1">
                    <span className="w-1 h-1 rounded-full bg-stone-300 flex-shrink-0" />
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>

            {/* Quick facts */}
            <div className="border border-stone-200 p-5 bg-white">
              <p className="text-xs uppercase tracking-widest font-semibold text-stone-400 mb-4">Quick Facts</p>
              <div className="space-y-3">
                {[
                  { label: "Total apartments", value: "208" },
                  { label: "Blocks", value: "6 (T1–T6)" },
                  { label: "Completed & occupied", value: "T1, T2, T3" },
                  { label: "Under construction", value: "T4, T5, T6" },
                  { label: "Delivery date", value: "September 2027" },
                  { label: "Location", value: "Noua, Brașov" },
                  { label: "Developer experience", value: "15 years" },
                  { label: "Heating system", value: "Solar + heat pump" },
                ].map((f, i) => (
                  <div key={i} className="flex justify-between items-start py-2 border-b border-stone-100 last:border-0">
                    <span className="text-stone-500 text-xs font-light pr-2">{f.label}</span>
                    <span className="text-stone-800 text-xs font-semibold text-right">{f.value}</span>
                  </div>
                ))}
              </div>
              <a href="https://www.cartiertransilvania.ro/en/" target="_blank" rel="noopener noreferrer"
                className="mt-4 flex items-center justify-center gap-1.5 w-full text-xs text-white py-2.5 transition-opacity hover:opacity-90"
                style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
                VISIT PROJECT SITE <ExternalLink size={11} />
              </a>
            </div>

            {/* Location */}
            <div className="border border-stone-200 p-5 bg-white">
              <p className="text-xs uppercase tracking-widest font-semibold text-stone-400 mb-3">Location</p>
              <a href="https://maps.google.com/?q=Strada+Nucului+48,+500306+Brasov,+Romania" target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 text-stone-600 text-sm font-light hover:text-stone-900 transition-colors group">
                <MapPin size={14} className="flex-shrink-0 mt-0.5" style={{ color: "oklch(0.38 0.12 15)" }} />
                <div>
                  <p className="font-semibold text-stone-800 text-sm group-hover:underline">Strada Nucului 48, Noua, Brașov</p>
                  <p className="text-xs mt-1 leading-relaxed">Next to Lake Noua, Brașov Zoo, and Adventure Park. Well-connected to city centre by public transport.</p>
                  <p className="text-xs mt-1" style={{ color: "oklch(0.38 0.12 15)" }}>View on Google Maps →</p>
                </div>
              </a>
            </div>
              {/* Newsletter */}
              <div className="border border-stone-200 p-5">
                <h4 className="font-bold text-stone-900 mb-2 text-sm" style={{ fontFamily: "Playfair Display, serif" }}>Stay in the loop</h4>
                <p className="text-stone-500 text-xs font-light leading-relaxed mb-3">No spam. One email per week with what is actually happening in Brașov.</p>
                <NewsletterInlineLight />
              </div>
              {/* CTA card — Relocation support */}
              <div className="p-5 text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
                <p className="text-xs uppercase tracking-widest mb-3 opacity-70">Relocation support</p>
                <h4 className="font-bold mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
                  Moving to Brașov?
                </h4>
                <p className="text-sm font-light leading-relaxed mb-4 opacity-80">
                  We help expats find apartments, navigate bureaucracy, and settle in — without the stress.
                </p>
                <Link href="/contact">
                  <button className="w-full text-xs py-2.5 border border-white/40 hover:bg-white/10 transition-colors tracking-widest">
                    BOOK A FREE CALL
                  </button>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <SiteFooter />
    </div>
    </>
  );
}
